import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { captureWorkQueueCounterClick } from '../../utils/engageWorkQueueAnalytics';
import DashboardSurfaceCard from './DashboardSurfaceCard';

const tileLinkSx = {
  display: 'block',
  height: '100%',
  textDecoration: 'none',
  color: 'inherit',
};

const tileCardSx = {
  '&:hover': {
    boxShadow: 3,
  },
};

const spinnerBoxSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 56,
};

function readAccessibleName(label, loading, error, count) {
  if (loading) {
    return `${label}, loading`;
  }
  if (error) {
    return `${label}, unavailable`;
  }
  return `${label}, ${count}`;
}

function WorkQueueCounterTile({
  queueId,
  label,
  caption = null,
  to,
  count = 0,
  loading = false,
  error = null,
}) {
  const handleClick = useCallback(() => {
    captureWorkQueueCounterClick(queueId);
  }, [queueId]);

  const countColor = error
    ? 'error.main'
    : count > 0
      ? 'warning.main'
      : 'text.primary';
  const displayedCount = error ? '—' : count.toLocaleString();

  return (
    <Link
      component={RouterLink}
      to={to}
      underline="none"
      color="inherit"
      aria-label={readAccessibleName(label, loading, error, count)}
      onClick={handleClick}
      sx={tileLinkSx}
    >
      <DashboardSurfaceCard sx={tileCardSx}>
        <CardContent>
          {loading ? (
            <Box sx={spinnerBoxSx}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography variant="h4" component="div" sx={{ color: countColor }}>
              {displayedCount}
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {caption ? (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {caption}
            </Typography>
          ) : null}
        </CardContent>
      </DashboardSurfaceCard>
    </Link>
  );
}

WorkQueueCounterTile.propTypes = {
  queueId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  caption: PropTypes.string,
  to: PropTypes.string.isRequired,
  count: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default WorkQueueCounterTile;
